import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';
import {
  Video, Users, Star, Calendar, Plus, Clock, BookOpen,
  AlertCircle, XCircle, X, Save,
} from 'lucide-react';
import SessionAnalyticsModal from '../components/SessionAnalyticsModal';

/* ── Status banner: pending / approved / rejected ── */
function ApprovalBanner({ status, reason }) {
  if (status === 'approved') return null; // no banner needed once approved

  const isPending = status === 'pending';
  const bg = isPending ? 'rgba(217,119,6,.06)' : 'rgba(239,68,68,.06)';
  const border = isPending ? 'rgba(217,119,6,.2)' : 'rgba(239,68,68,.2)';
  const color = isPending ? '#d97706' : '#ef4444';
  const Icon = isPending ? AlertCircle : XCircle;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, background: bg, border: `1px solid ${border}`,
      borderRadius: 16, padding: '14px 18px', marginBottom: 16,
    }}>
      <Icon size={20} color={color} style={{ flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
          {isPending ? 'Your mentor profile is awaiting admin review' : 'Your mentor application was not approved'}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          {isPending
            ? "You can already fill out your profile below — session hosting unlocks once an admin approves you."
            : (reason || 'Contact support for details.')}
        </div>
      </div>
    </div>
  );
}

/* ── Stat card, matching Dashboard.jsx's StatCard visual language ── */
function StatCard({ icon: Icon, iconBg, label, value, color }) {
  return (
    <div style={{
      background: '#ffffff', border: '1px solid rgba(0,0,0,.05)', borderRadius: 20, padding: 20,
      position: 'relative', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,.02)',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '20px 20px 0 0' }} />
      <div style={{
        width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: iconBg, marginBottom: 14, boxShadow: '0 4px 10px rgba(0,0,0,.08)',
      }}>
        <Icon size={20} color="#ffffff" />
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--navy-dark)' }}>{value}</div>
    </div>
  );
}

/* ── Editable mentor profile card ── */
function MentorProfileCard({ mentorProfile, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    subjects: (mentorProfile?.subjects || []).join(', '),
    qualification: mentorProfile?.qualification || '',
    bio: mentorProfile?.bio || '',
    hourlyRateInr: mentorProfile?.hourlyRateInr || 0,
    availability: mentorProfile?.availability || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      subjects: (mentorProfile?.subjects || []).join(', '),
      qualification: mentorProfile?.qualification || '',
      bio: mentorProfile?.bio || '',
      hourlyRateInr: mentorProfile?.hourlyRateInr || 0,
      availability: mentorProfile?.availability || '',
    });
  }, [mentorProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        subjects: form.subjects.split(',').map((s) => s.trim()).filter(Boolean),
        qualification: form.qualification,
        bio: form.bio,
        hourlyRateInr: Number(form.hourlyRateInr) || 0,
        availability: form.availability,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 10, outline: 'none', fontFamily: 'inherit', fontSize: 13, color: '#0f172a',
  };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, display: 'block' };

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,.05)', borderRadius: 20, padding: 20, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#8b5cf6,#a78bfa)', borderRadius: '20px 20px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-dark)' }}>Mentor Profile</span>
        {!editing && (
          <button onClick={() => setEditing(true)} style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer' }}>
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Subjects (comma-separated)</label>
            <input style={inputStyle} value={form.subjects} onChange={(e) => setForm({ ...form, subjects: e.target.value })} placeholder="DSA, Physics, Calculus" />
          </div>
          <div>
            <label style={labelStyle}>Qualification</label>
            <input style={inputStyle} value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="M.Tech CSE, IIT Hyderabad" />
          </div>
          <div>
            <label style={labelStyle}>Bio</label>
            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Hourly Rate (₹, 0 = free)</label>
              <input type="number" style={inputStyle} value={form.hourlyRateInr} onChange={(e) => setForm({ ...form, hourlyRateInr: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Availability</label>
              <input style={inputStyle} value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="Weekday evenings" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)}
              style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={labelStyle}>Subjects</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(mentorProfile?.subjects || []).length > 0 ? mentorProfile.subjects.map((s) => (
                <span key={s} style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', background: 'rgba(139,92,246,.08)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 20, padding: '3px 10px' }}>{s}</span>
              )) : <span style={{ fontSize: 12, color: '#94a3b8' }}>No subjects added yet</span>}
            </div>
          </div>
          <div><div style={labelStyle}>Qualification</div><div style={{ fontSize: 13, color: '#334155' }}>{mentorProfile?.qualification || '—'}</div></div>
          <div><div style={labelStyle}>Bio</div><div style={{ fontSize: 13, color: '#334155' }}>{mentorProfile?.bio || '—'}</div></div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div><div style={labelStyle}>Rate</div><div style={{ fontSize: 13, color: '#334155' }}>{mentorProfile?.hourlyRateInr ? `₹${mentorProfile.hourlyRateInr}/hr` : 'Free'}</div></div>
            <div><div style={labelStyle}>Availability</div><div style={{ fontSize: 13, color: '#334155' }}>{mentorProfile?.availability || '—'}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Create-session modal ── */
function CreateSessionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', subject: '', description: '', date: '', time: '', duration: '60 min', level: 'Beginner', total_capacity: 20, price: 'Free', meetingLink: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 10, outline: 'none', fontFamily: 'inherit', fontSize: 13, color: '#0f172a',
  };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, display: 'block' };
  const MEETING_LINK_PATTERN = /^https:\/\/(meet\.google\.com\/|teams\.microsoft\.com\/|teams\.live\.com\/)/i;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!MEETING_LINK_PATTERN.test(form.meetingLink)) {
      setError('Please paste a valid Google Meet or Microsoft Teams link');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/mentors/sessions', form);
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not create session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflowY: 'auto' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 20, padding: 24, width: '92%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy-dark)' }}>Create a Session</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 700, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 10, padding: 10, marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><label style={labelStyle}>Title</label><input style={inputStyle} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label style={labelStyle}>Subject</label><input style={inputStyle} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Data Structures" /></div>
          <div><label style={labelStyle}>Description</label><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><label style={labelStyle}>Time</label><input type="time" style={inputStyle} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Duration</label><input style={inputStyle} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
            <div><label style={labelStyle}>Capacity</label><input type="number" style={inputStyle} value={form.total_capacity} onChange={(e) => setForm({ ...form, total_capacity: e.target.value })} /></div>
          </div>
          <div>
            <label style={labelStyle}>Meeting Link (Google Meet or Microsoft Teams)</label>
            <input style={inputStyle} required value={form.meetingLink} onChange={(e) => setForm({ ...form, meetingLink: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij" />
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Students will use this exact link to join once you start the session.</div>
          </div>
          <button type="submit" disabled={saving}
            style={{ marginTop: 4, background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating...' : 'Create Session'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Session row ── */
function SessionRow({ session }) {
  const [showAnalytics, setShowAnalytics] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,.04)', flexWrap: 'wrap' }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#8b5cf6,#a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <BookOpen size={18} color="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.title}</div>
        <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 10, marginTop: 2 }}>
          {session.date && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={11} />{new Date(session.date).toLocaleDateString()}</span>}
          {session.time && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} />{session.time}</span>}
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', flexShrink: 0 }}>{session.enrolled_count}/{session.total_capacity}</div>
      <button onClick={() => setShowAnalytics(true)} className="btn-lift"
        style={{ fontSize: 10, fontWeight: 700, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 }}>
        Analytics
      </button>
      {showAnalytics && <SessionAnalyticsModal sessionId={session.id} type="mentor" onClose={() => setShowAnalytics(false)} />}
    </div>
  );
}

/* ── Main page ── */
export default function MentorDashboard() {
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [mentorProfile, setMentorProfile] = useState(null);
  const [sessions, setSessions] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [mp, s] = await Promise.all([
        apiClient.get('/mentors/me/profile'),
        apiClient.get('/mentors/me/sessions'),
      ]);
      setMentorProfile(mp);
      setSessions(s);
    } catch (e) {
      // fine on first load before a profile exists — handled by the banner below
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    if (user.role !== 'mentor' && user.role !== 'admin') { navigate('/dashboard'); return; }
    loadAll();
  }, [user, authLoading, navigate, loadAll]);

  const handleProfileSave = async (updates) => {
    const updated = await apiClient.patch('/mentors/me/profile', updates);
    setMentorProfile(updated);
  };

  if (authLoading || loading) {
    return <div style={{ padding: '6rem 2rem', textAlign: 'center', color: '#64748b' }}>Loading your mentor dashboard...</div>;
  }

  const name = profile?.full_name || profile?.name || 'Mentor';
  const status = mentorProfile?.status || 'pending';

  return (
    <div style={{ minHeight: '100vh', background: '#faf5ff' }}>
      {/* Minimal topbar — the global marketing Header is hidden on this route,
          same pattern Dashboard.jsx uses for its own sidebar/topbar shell. */}
      <header style={{ background: '#ffffff', borderBottom: '1px solid rgba(0,0,0,.05)', padding: '0 20px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/Logo.jpeg" alt="Ekalavya" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--navy-dark)' }}>Ekalavya <span style={{ color: '#8b5cf6' }}>Mentor</span></span>
        </div>
        <button
          onClick={() => { signOut(); navigate('/'); }}
          style={{ background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.15)', color: '#ef4444', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
        >
          Sign Out
        </button>
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem 4rem', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Hero banner, matching Dashboard.jsx's hero style */}
      <div style={{
        background: 'linear-gradient(135deg, #f5f3ff 0%, #e0e7ff 100%)', borderRadius: 20, padding: '24px 28px',
        border: '1px solid rgba(147,51,234,.1)', boxShadow: '0 10px 30px rgba(147,51,234,.05)',
      }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(147,51,234,.06)', border: '1px solid rgba(147,51,234,.12)', borderRadius: 20, padding: '4px 12px', fontSize: 9, fontWeight: 700, color: '#8b5cf6', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
          Mentor Dashboard
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--navy-dark)', marginBottom: 4 }}>Welcome, {name} 👋</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {status === 'approved' ? 'Manage your sessions and grow your mentoring impact.' : 'Complete your profile below while your application is reviewed.'}
        </div>
      </div>

      <ApprovalBanner status={status} reason={mentorProfile?.rejectionReason} />

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <StatCard icon={Video} iconBg="linear-gradient(145deg,#60a5fa,#2563eb)" color="linear-gradient(90deg,#2563eb,#60a5fa)" label="Sessions Hosted" value={mentorProfile?.totalSessionsHosted ?? 0} />
        <StatCard icon={Users} iconBg="linear-gradient(145deg,#34d399,#059669)" color="linear-gradient(90deg,#059669,#34d399)" label="Students Taught" value={mentorProfile?.totalStudentsTaught ?? 0} />
        <StatCard icon={Star} iconBg="linear-gradient(145deg,#fbbf24,#d97706)" color="linear-gradient(90deg,#d97706,#fbbf24)" label="Average Rating" value={mentorProfile?.averageRating ? mentorProfile.averageRating.toFixed(1) : '—'} />
        <StatCard icon={Calendar} iconBg="linear-gradient(145deg,#c084fc,#8b5cf6)" color="linear-gradient(90deg,#8b5cf6,#c084fc)" label="Upcoming Sessions" value={sessions.upcoming.length} />
      </div>

      <div className="mentor-dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        {/* Sessions list */}
        <div style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,.05)', borderRadius: 20, padding: 20, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#8b5cf6,#fb923c)', borderRadius: '20px 20px 0 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-dark)' }}>My Sessions</span>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={status !== 'approved'}
              title={status !== 'approved' ? 'Approval required before you can create sessions' : ''}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: status === 'approved' ? '#8b5cf6' : '#e2e8f0',
                color: status === 'approved' ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, padding: '8px 14px',
                fontSize: 12, fontWeight: 700, cursor: status === 'approved' ? 'pointer' : 'not-allowed',
              }}
            >
              <Plus size={14} /> New Session
            </button>
          </div>
          {sessions.upcoming.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>No upcoming sessions yet.</div>
          ) : sessions.upcoming.map((s) => <SessionRow key={s.id} session={s} />)}
        </div>

        <MentorProfileCard mentorProfile={mentorProfile} onSave={handleProfileSave} />
      </div>

      {showCreateModal && <CreateSessionModal onClose={() => setShowCreateModal(false)} onCreated={loadAll} />}
      </div>
    </div>
  );
}
